import { useCallback, useEffect, useRef, useState } from 'react'
import { showNotification } from '@mantine/notifications'
import { useClient, type OrpcClient } from '@/hooks/useClient'
import { logError } from '@/utils/errorUtils'

export type KateCheckIn = Awaited<ReturnType<OrpcClient['kateCheckInGet']>>
const LIVE_POLL_MS = 15_000
export type KateCheckInAction = 'send' | 'copy' | 'review'

/**
 * The Kate check-in of one appointment for the schedule card: derived status,
 * timestamps and the filed answers (`kateCheckInGet`), plus the three staff
 * actions. All server-derived — the card only renders what comes back.
 */
export function useKateCheckIn(appointmentId: string | undefined, appointmentStatus?: string) {
  const client = useClient()
  const [data, setData] = useState<KateCheckIn | null>(null)
  // A failed load must not read as "nothing has happened": the card would claim
  // "Not sent" for a check-in the patient has already done, and staff would
  // resend or phone them.
  const [loadFailed, setLoadFailed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<KateCheckInAction | null>(null)
  // The link minted by the last send in this preview, so "Copy link" right after
  // "Send" does not mint a second one.
  const [link, setLink] = useState<string | null>(null)
  // Reads can finish out of order (a poll in flight when a status change triggers
  // a re-read); only the newest request may write.
  const readSeq = useRef(0)

  const refresh = useCallback(async () => {
    if (!appointmentId) {
      // No appointment behind this slot: there is no check-in to report on, and
      // claiming "not sent" would invite staff to send one that cannot exist.
      setData(null)
      setLoadFailed(true)
      return
    }
    const seq = ++readSeq.current
    setLoading(true)
    try {
      const next = await client.kateCheckInGet({ appointmentId })
      if (seq !== readSeq.current) return
      setData(next)
      setLoadFailed(false)
    } catch (error) {
      if (seq !== readSeq.current) return
      logError('schedule', 'Load Kate check-in', error)
      // Last known state stays on screen; the card shows the failure instead of
      // inventing one.
      setLoadFailed(true)
    } finally {
      if (seq === readSeq.current) setLoading(false)
    }
  }, [appointmentId, client])

  // The server derives the no-show follow-up from the appointment's status, so a
  // status change in the open preview must re-read the card.
  useEffect(() => {
    setLink(null)
    void refresh()
  }, [refresh, appointmentStatus])

  // While the patient can still move the check-in along, keep the open card current.
  const live =
    data?.status === 'sent' || data?.status === 'opened' || data?.status === 'in_progress'
  useEffect(() => {
    if (!live) return
    const id = window.setInterval(() => void refresh(), LIVE_POLL_MS)
    return () => window.clearInterval(id)
  }, [live, refresh])

  const failed = (action: string, error: unknown) => {
    logError('schedule', `Kate check-in ${action}`, error)
    showNotification({
      color: 'red',
      message: (error as Error)?.message || `Could not ${action} the Kate check-in.`,
    })
  }

  const send = useCallback(
    async (mode: 'check-in' | 'rebooking' = 'check-in') => {
      if (!appointmentId) return
      const what = mode === 'rebooking' ? 'follow-up' : 'check-in'
      setBusy('send')
      try {
        const result = await client.kateCheckInSend({ appointmentId, deliver: true, mode })
        setLink(result.link)
        const via = result.delivered.map((c) => (c === 'sms' ? 'SMS' : 'email')).join(' and ')
        showNotification({
          color: result.delivered.length ? 'green' : 'yellow',
          message: result.delivered.length
            ? `Kate ${what} sent by ${via}.`
            : 'Link created, but the patient has no phone or email to deliver it to.',
        })
        await refresh()
      } catch (error) {
        failed('send', error)
      } finally {
        setBusy(null)
      }
    },
    [appointmentId, client, refresh],
  )

  const copyLink = useCallback(async () => {
    if (!appointmentId) return
    setBusy('copy')
    try {
      const value = link ?? (await client.kateCheckInSend({ appointmentId, deliver: false })).link
      setLink(value)
      await navigator.clipboard.writeText(value)
      showNotification({ color: 'green', message: 'Kate check-in link copied.' })
      await refresh()
    } catch (error) {
      failed('copy', error)
    } finally {
      setBusy(null)
    }
  }, [appointmentId, client, link, refresh])

  const review = useCallback(async () => {
    if (!appointmentId) return
    setBusy('review')
    try {
      await client.kateCheckInReview({ appointmentId })
      showNotification({ color: 'green', message: 'Kate responses marked as reviewed.' })
      await refresh()
    } catch (error) {
      failed('review', error)
    } finally {
      setBusy(null)
    }
  }, [appointmentId, client, refresh])

  return { data, loading, loadFailed, busy, send, copyLink, review, refresh }
}

export type KateCheckInState = ReturnType<typeof useKateCheckIn>
